import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Subject, throwError, iif, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { NotFoundError, AppError } from '@mt-ng2/errors-module';
import { MtSearchFilterItem } from '@mt-ng2/search-filter-select-control';
import { ExtraSearchParams } from '@mt-ng2/common-classes';

const defaultCreateOptions = {
    nullOutFKs: true,
};
// tslint:disable-next-line:max-line-length
const updatePartialErrorMessage = '@mt-ng2/base-service : updatePartial failed while trying to JSON.stringify() the passed in object with id';
class BaseService {
    constructor(baseurl, http) {
        this.baseurl = baseurl;
        this.http = http;
        this.emitChangeSource = new Subject();
        this.changeEmitted$ = this.emitChangeSource.asObservable();
    }
    emitChange(entity) {
        this.emitChangeSource.next(entity);
    }
    get(searchparameters) {
        let params = this.getHttpParams(searchparameters);
        return this.http
            .get(this.baseurl + '/_search', {
            observe: 'response',
            params: params,
        })
            .pipe(catchError(this.handleError));
    }
    getAll() {
        return this.http.get(this.baseurl).pipe(catchError(this.handleError));
    }
    getById(id) {
        return this.http.get(this.baseurl + '/' + id).pipe(catchError(this.handleError));
    }
    create(object, options = defaultCreateOptions) {
        let clone = JSON.parse(JSON.stringify(object));
        if (options.nullOutFKs) {
            clone = this.nullOutFKs(clone);
        }
        return this.http.post(this.baseurl, clone).pipe(catchError(this.handleError));
    }
    createWithFks(object) {
        return this.create(object, { nullOutFKs: false });
    }
    delete(id) {
        return this.http.delete(this.baseurl + '/' + id).pipe(catchError(this.handleError));
    }
    /**
     * Calls the CRUD Delete and checks for concurrency, needs Id and Version
     * @param object
     */
    deleteVersion(object) {
        const clone = JSON.parse(JSON.stringify(object));
        return this.http.request('delete', this.baseurl + '/' + clone.Id + '/version', { body: clone }).pipe(catchError(this.handleError));
    }
    update(object) {
        let clone = JSON.parse(JSON.stringify(object));
        clone = this.nullOutFKs(clone);
        return this.http.put(this.baseurl + '/' + clone.Id, clone).pipe(catchError(this.handleError));
    }
    updateWithFks(object) {
        return this.http.put(this.baseurl + '/' + object.Id, object).pipe(catchError(this.handleError));
    }
    /**
     * Calls the CRUD Update and checks for concurrency
     * @param object
     */
    updateVersion(object) {
        let clone = JSON.parse(JSON.stringify(object));
        clone = this.nullOutFKs(clone);
        return this.http.put(this.baseurl + '/' + clone.Id + '/version', clone).pipe(catchError((err) => this.handleError(err, clone)));
    }
    /**
     * Calls the CRUD Update and checks for concurrency
     * @param object
     */
    updateVersionWithFks(object) {
        return this.http.put(this.baseurl + '/' + object.Id + '/version', object).pipe(catchError((err) => this.handleError(err, object)));
    }
    /**
     * Updates the entity when you don't have all the properties
     * to pass in
     * @param object
     * @param id
     */
    updatePartial(object, id) {
        return this._updatePartial(object, id, false);
    }
    /**
     * Updates the Versionable entity when you don't have all the properties
     * to pass in, the object string must include the Version
     * @param object
     * @param id
     */
    updatePartialVersionable(object, id) {
        return this._updatePartial(object, id, true);
    }
    _updatePartial(object, id, versionable) {
        let objectAsString;
        try {
            // Adding the single quotes before and after because of .NET's String Parser
            // We are reading this as a string because we can't serialize it to an object.
            // When this gets sent to the backend .NET can't convert it to a string without
            // the single quotes.
            objectAsString = `'${JSON.stringify(object)}'`;
        }
        catch (error) {
            throw new Error(updatePartialErrorMessage + ' ' + id);
        }
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });
        const endPointUrl = versionable ? `${this.baseurl}/${id}/version` : `${this.baseurl}/${id}`;
        return this.http.patch(endPointUrl, objectAsString, { headers }).pipe(catchError(this.handleError));
    }
    /**
     * Calls the CRUD Service doing a merge on the list properties
     * @param object
     * @param id
     */
    updateList(object) {
        return this.http.put(this.baseurl, object).pipe(catchError(this.handleError));
    }
    handleError(error, formObject) {
        if (error.status === 400) {
            return throwError(error);
        }
        if (error.status === 404) {
            return throwError(new NotFoundError());
        }
        return throwError(new AppError(error, formObject));
    }
    /**
     * Convenience method for formatting the SearchParams object from the @mt-ng2/common-classes
     * package into the expected backend object
     * @param searchparameters
     */
    getHttpParams(searchparameters) {
        let params = new HttpParams();
        if (searchparameters.query) {
            params = params.append('query', searchparameters.query);
        }
        if (searchparameters.skip) {
            params = params.append('skip', searchparameters.skip.toString());
        }
        if (searchparameters.take) {
            params = params.append('take', searchparameters.take.toString());
        }
        if (searchparameters.order) {
            params = params.append('order', searchparameters.order.toString());
        }
        if (searchparameters.orderDirection) {
            params = params.append('orderDirection', searchparameters.orderDirection.toString());
        }
        if (searchparameters.extraParams && searchparameters.extraParams.length > 0) {
            let extraparams = new HttpParams();
            searchparameters.extraParams.forEach((param) => {
                if (param.valueArray) {
                    if (param.valueArray.length > 0) {
                        extraparams = extraparams.append(param.name, param.valueArray.toString());
                    }
                }
                else {
                    if (param.value.length > 0) {
                        extraparams = extraparams.set(param.name, param.value);
                    }
                }
            });
            if (extraparams.keys().length > 0) {
                params = params.append('extraparams', extraparams.toString());
            }
        }
        return params;
    }
    /**
     * Clear out the FK objects so that EF doesn't try to update
     * them unintentionally
     * @param object
     */
    nullOutFKs(object) {
        for (let prop in object) {
            if (typeof object[prop] === 'object') {
                object[prop] = null;
            }
        }
        return object;
    }
}

class MetaItemService extends BaseService {
    constructor(serviceName, objectName, parameterName, metabaseurl, http) {
        super(metabaseurl, http);
        this.serviceName = serviceName;
        this.objectName = objectName;
        this.parameterName = parameterName;
        this.metabaseurl = metabaseurl;
        this.http = http;
        this._itemsSubject = new Subject();
        this._callinProgress = false;
        /**
         * @deprecated Do not use the items property from the MetaItemService. If preserving items, use the StaticMetaItemService instead.
         */
        this.items = [];
        this.MetaItemListServiceInitilizer = {
            getItems: () => {
                return this.getItems().pipe(map((items) => {
                    let answer = new MetaItemListDefinition(items.map((item) => new MtSearchFilterItem(item, false)), items.map((item) => new MetaItem(item.Id, item.Name)), this.objectName, this.parameterName, this.serviceName);
                    return answer;
                }));
            },
        };
    }
    updateItems(items) {
        return this.http.put(`${this.metabaseurl}/update`, items);
    }
    getItems() {
        return iif(this.returnItemsAsObservable.bind(this), this.getSubject()
            .asObservable()
            .pipe(take(1)), this.getSubject()
            .asObservable()
            .pipe(take(1)));
    }
    getSearchFilterItems() {
        return this.getItems().pipe(map((items) => {
            return items.map((item) => new MtSearchFilterItem(item, false));
        }));
    }
    getSubject() {
        if (!this._itemsSubject) {
            this._itemsSubject = new Subject();
        }
        return this._itemsSubject;
    }
    returnItemsAsObservable() {
        if (!this._callinProgress) {
            this._callinProgress = true;
            this.getAll().subscribe((items) => {
                this._itemsSubject.next(items);
                this._callinProgress = false;
            });
        }
        return false;
    }
}
class StaticMetaItemService extends MetaItemService {
    constructor(serviceName, objectName, parameterName, staticmetabaseurl, http) {
        super(serviceName, objectName, parameterName, staticmetabaseurl, http);
        this.serviceName = serviceName;
        this.objectName = objectName;
        this.parameterName = parameterName;
        this.staticmetabaseurl = staticmetabaseurl;
        this.http = http;
        this._itemsStored = false;
        this.items = [];
        this.items = null;
    }
    getItems() {
        return iif(this.returnItemsAsObservableStatic.bind(this), of(this.items), this.getSubject()
            .asObservable()
            .pipe(take(1)));
    }
    returnItemsAsObservableStatic() {
        if (this.items) {
            return true;
        }
        if (!this._callinProgress) {
            this._callinProgress = true;
            this.getAll().subscribe((items) => {
                this.items = items;
                this._itemsSubject.next(items);
                this._callinProgress = false;
            });
        }
        return false;
    }
}
class MetaItemListDefinition {
    constructor(FilterItems, Items, Name, ParameterName, ServiceName) {
        this.FilterItems = FilterItems;
        this.Items = Items;
        this.Name = Name;
        this.ParameterName = ParameterName;
        this.ServiceName = ServiceName;
    }
    getSelectedFilterItems(filterItems) {
        return filterItems.filter((item) => item.Selected).map((item) => item.Item.Id);
    }
    toExtraSearchParams() {
        return new ExtraSearchParams({
            name: this.ParameterName,
            valueArray: this.getSelectedFilterItems(this.FilterItems),
        });
    }
}
class MetaItem {
    constructor(Id, Name) {
        this.Id = Id;
        this.Name = Name;
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { BaseService, MetaItem, MetaItemListDefinition, MetaItemService, StaticMetaItemService, updatePartialErrorMessage };

//# sourceMappingURL=mt-ng2-base-service.js.map